import {
  Box,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
  styled,
} from '@mui/material';
import HomeServices from './components/HomeServices';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from './../../utils/api';
import ProductCategorySkeleton from './components/ProductCategoryLoading';
import { usePageTitle } from '../../context/PageTitleContext';

const Header = styled('div')(({ theme, smDown, mdDown, lgDown, xlDown }) => {
  let fontSize = '26px';
  let lineHeight = '36px';
  let fontWeight = 600;

  if (smDown) {
    fontSize = '16px';
    lineHeight = '24px';
    fontWeight = 700;
  } else if (mdDown) {
    fontSize = '20px';
    lineHeight = '28px';
  } else if (lgDown) {
    fontSize = '18px';
    lineHeight = '32px';
  } else if (xlDown) {
    fontSize = '20px';
    lineHeight = '34px';
  }

  return {
    width: '100%',
    fontSize: `${fontSize} !important`,
    fontStyle: 'normal',
    fontWeight,
    lineHeight: `${lineHeight} !important`,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(4),
    paddingBottom: 0,
    borderBottom: 'none',
  };
});

const HomePlayground = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  usePageTitle('Playground');
  const mdDown = useMediaQuery(theme.breakpoints.down('md'));
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));
  const lgDown = useMediaQuery(theme.breakpoints.down('lg'));
  const xlDown = useMediaQuery(theme.breakpoints.down('xl'));
  const [loading, setLoading] = useState(true);
  const [servicesGroups, setServicesGroups] = useState([]);

  const getServiceList = async () => {
    const serviceListReq = await api.call({
      name: 'publicServiceList',
    });

    if (serviceListReq.status === 200) {
      const serviceListData = serviceListReq.data.data;

      const iconMapping = {
        'resume-parser': {
          dark: 'resume-parser-dark-new.svg',
          light: 'resume-parser-light-new.svg',
          hoverDark: 'resume-parser-dark-new-hover.svg',
          hoverLight: 'resume-parser-light-new-hover.svg',
        },
        'resume-review': {
          dark: 'resume-review-dark.svg',
          light: 'resume-review.svg',
          hoverDark: 'resume-review-icon-dark.svg',
          hoverLight: 'resume-review-icon-light.svg',
        },
        'resume-score': {
          dark: 'resume-score-dark.svg',
          light: 'resume-score.svg',
          hoverDark: 'resume-score-icon-dark.svg',
          hoverLight: 'resume-score-icon-light.svg',
        },
        'company-data': {
          dark: 'company-data-dark.svg',
          light: 'company-data.svg',
          hoverDark: 'company-data-icon-dark.svg',
          hoverLight: 'company-data-icon-light.svg',
        },
        'profile-data': {
          dark: 'profile-data-dark.svg',
          light: 'profile-data.svg',
          hoverDark: 'profile-data-icon-dark.svg',
          hoverLight: 'profile-data-icon-light.svg',
        },
      };

      const enhancedServices = serviceListData.map((service) => ({
        ...service,
        icon_url_dark: iconMapping[service.key]?.dark || service.icon_url_dark,
        icon_url_light:
          iconMapping[service.key]?.light || service.icon_url_light,
        icon_hover_url_dark:
          iconMapping[service.key]?.hoverDark || service.icon_url_dark,
        icon_hover_url_light:
          iconMapping[service.key]?.hoverLight || service.icon_url_light,
      }));

      // Grouping based on Figma design
      const groups = [
        {
          title: 'sidebar.resumeServices',
          services: enhancedServices
            .filter((service) =>
              ['resume-parser', 'resume-review', 'resume-score'].includes(
                service.key
              )
            )
            .sort((a, b) => {
              const order = ['resume-parser', 'resume-review', 'resume-score'];
              return order.indexOf(a.key) - order.indexOf(b.key);
            }),
        },
        {
          title: 'sidebar.dataServices',
          services: enhancedServices
            .filter((service) =>
              ['profile-data', 'company-data'].includes(service.key)
            )
            .sort((a, b) => {
              const order = ['profile-data', 'company-data'];
              return order.indexOf(a.key) - order.indexOf(b.key);
            }),
        },
      ];

      setServicesGroups(groups);
      setLoading(false);
    }
  };

  useEffect(() => {
    getServiceList();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          py: 4,
          px: 4,
        }}
      >
        {!mdDown && (
          <Header
            smDown={smDown}
            mdDown={mdDown}
            lgDown={lgDown}
            xlDown={xlDown}
          >
            Playground
          </Header>
        )}
        <Box
          sx={{
            width: '100%',
            backgroundColor: theme.palette.mode === 'dark' ? '#262626' : '#fff',
            border:
              theme.palette.mode === 'dark'
                ? '1px solid #464646'
                : `1px solid ${theme.palette.secondary.neutral_90}`,
            borderRadius: '19px',
            px: mdDown ? 2 : 4,
            py: mdDown ? 4 : 8,
            display: 'flex',
            flexDirection: 'column',
            gap: mdDown ? 4 : 14,
          }}
        >
          {[
            { title: 'sidebar.resumeServices', count: 3 },
            { title: 'sidebar.dataServices', count: 2 },
          ].map((group, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: mdDown ? 4 : 6,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontSize: mdDown ? '16px' : '20px',
                  fontWeight: 500,
                  color: theme.palette.mode === 'dark' ? '#fff' : '#1A1B1F',
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
                {t(group.title)}
              </Typography>
              <Grid container spacing={mdDown ? 4 : 6}>
                {Array.from({ length: group.count }).map((_, i) => (
                  <Grid item xs={12} md={4} key={i}>
                    <ProductCategorySkeleton />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        py: 4,
        px: 4,
      }}
    >
      {!mdDown && (
        <Header smDown={smDown} mdDown={mdDown} lgDown={lgDown} xlDown={xlDown}>
          Playground
        </Header>
      )}
      <Box
        sx={{
          width: '100%',
          backgroundColor: theme.palette.mode === 'dark' ? '#262626' : '#fff',
          border:
            theme.palette.mode === 'dark'
              ? '1px solid #464646'
              : `1px solid ${theme.palette.secondary.neutral_90}`,
          borderRadius: '19px',
          px: mdDown ? 2 : 4,
          py: mdDown ? 4 : 8,
          display: 'flex',
          flexDirection: 'column',
          gap: mdDown ? 4 : 14,
        }}
      >
        {servicesGroups.map((group, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: mdDown ? 4 : 6,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontSize: mdDown ? '16px' : '20px',
                fontWeight: 500,
                color: theme.palette.mode === 'dark' ? '#fff' : '#1A1B1F',
                fontFamily: 'Poppins, sans-serif',
              }}
            >
              {t(group.title)}
            </Typography>
            <Grid container spacing={mdDown ? 4 : 6}>
              {group.services.map((service) => (
                <Grid item xs={12} md={4} key={service.key}>
                  <HomeServices service={service} />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
export default HomePlayground;
